import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens } from '@fluentui/react-components';
import { Heading, Container } from '../../theme/primitives';
import { iconMap } from '../../theme/icons';
import { stonePalette } from '../../theme/brand';
import panelsImg from '../../assets/img/marketing/panels.jpg';

const CheckIcon = iconMap.check;

const useStyles = makeStyles({
  wrap: {
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: '1rem',
    paddingBottom: '1rem',
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  // 50 buvo per silpnas (#fafaf9 — beveik nepastebimas ant balto puslapio fono,
  // ypač produkto puslapiuose, kur šalia nėra jokios kitos spalvotos sekcijos
  // kontrastui, žr. /admin/bugs "background color reikia siam blokui (nereikia
  // tik home page)"). Grąžinta į 100 (#f4f3f1) — matomas visur, ne tik ten, kur
  // šalia atsitiktinai yra kontrastingas fonas.
  card: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    borderRadius: tokens.borderRadiusXLarge,
    overflow: 'hidden',
    backgroundColor: stonePalette[100],
    // 191px → +50px (žr. /admin/bugs "Padidink sio bloko auksti + 50px") →
    // +30px daugiau (žr. /admin/bugs "Padidint sio bloko auksti dar ~30px").
    '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(2, 1fr)', minHeight: '271px' },
  },
  // position:relative/absolute (ne grid stretch + natūralaus paveikslėlio
  // aukščio) — anksčiau <img> savo intrinsic proporcija DIKTAVO visos grid
  // eilutės (taigi ir turinio stulpelio) aukštį, todėl blokas būdavo daug
  // aukštesnis, nei trumpam sąrašui realiai reikia (žr. /admin/bugs "Sumazink
  // auksti sio bloko/sekcijos"). Dabar eilutės aukštį lemia TIK turinio
  // stulpelis, o paveikslėlis tiesiog užpildo tą aukštį per object-fit:cover.
  imageWrap: {
    display: 'none',
    position: 'relative',
    overflow: 'hidden',
    '@media (min-width: 640px)': { display: 'block' },
  },
  image: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  // Didesnis kairysis paddingas nuo kairiojo paveiksliuko (žr. /admin/bugs
  // "desiniosios turinis didesni padding nuo kairiojo paveiksliuko").
  content: {
    paddingTop: tokens.spacingHorizontalXXL, paddingBottom: tokens.spacingHorizontalXXL,
    paddingLeft: '2rem', paddingRight: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '@media (min-width: 640px)': { paddingTop: '1rem', paddingBottom: '1rem', paddingLeft: '2.5rem', paddingRight: '1.75rem' },
  },
  title: { marginBottom: tokens.spacingVerticalXS },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    marginTop: 0,
    marginBottom: tokens.spacingVerticalS,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  checkIcon: { color: tokens.colorBrandForeground2, flexShrink: 0, fontSize: '18px' },
  // fontWeight pašalintas — nuorodos neturi būti bold (žr. /admin/bugs "Zalios
  // nuorodos neturi buti bold").
  link: {
    color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'underline',
    textUnderlineOffset: '2px',
    ':hover': { color: tokens.colorBrandForegroundLinkHover, textDecorationLine: 'none' },
  },
});

const HowToChoosePanels = () => {
  const s = useStyles();
  const { t } = useTranslation();
  return (
    <Container size="xl" className={s.wrap}>
      <div className={s.card}>
        <div className={s.imageWrap}>
          <img src={panelsImg} alt={t('shop.how_to_choose_panels.img_alt')} className={s.image} />
        </div>
        <div className={s.content}>
          <Heading as="h2" variant="h2" className={s.title}>{t('shop.how_to_choose_panels.title')}</Heading>
          <h3 className={s.subtitle}>{t('shop.how_to_choose_panels.subtitle')}</h3>
          <ul className={s.list}>
            <li className={s.listItem}>
              <CheckIcon className={s.checkIcon} />
              <span>
                {t('shop.how_to_choose_panels.item_configurator_pre')}{' '}
                <Link to="/konfiguratorius" className={s.link}>{t('shop.how_to_choose_panels.item_configurator_link1')}</Link>
                {' '}{t('shop.how_to_choose_panels.item_configurator_or')}{' '}
                <Link to="/issirinkt-plokstes" className={s.link}>{t('shop.how_to_choose_panels.item_configurator_link2')}</Link>
              </span>
            </li>
            <li className={s.listItem}>
              <CheckIcon className={s.checkIcon} />
              <span>
                {t('shop.how_to_choose_panels.item_visit_pre')}{' '}
                <Link to="/kontaktai" className={s.link}>{t('shop.how_to_choose_panels.item_visit_link')}</Link>
              </span>
            </li>
            <li className={s.listItem}>
              <CheckIcon className={s.checkIcon} />
              <span>
                {t('shop.how_to_choose_panels.item_carpenter_pre')}{' '}
                <Link to="/meistro-uzsakymas" className={s.link}>{t('shop.how_to_choose_panels.item_carpenter_link')}</Link>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </Container>
  );
};

export default HowToChoosePanels;
